import { useEffect, useMemo, useRef, useState } from "react";
import Select, { components } from "react-select";
import Modal from "react-modal";
import moment from "moment";
import "moment/locale/es";
import MainButton from "../../components/MainButton/MainButton"
import { useForm, Controller, useWatch } from "react-hook-form";

import { getDailyByEmployee, saveTask } from "../../services/newdaily";
import styles from "./TaskCreationModal.module.css";

import CalendarIcon from "../../assets/svgs/calendar.svg";
import TimeIcon from "../../assets/svgs/time.svg";
import ClockIcon from "../../assets/svgs/clock.svg";
import LabelArrowIcon from "../../assets/svgs/label-arrow.svg";
import WarningFormIcon from "../../assets/svgs/warningFormIcon.svg";
import PeriodicityIcon from "../../assets/svgs/periodicity.svg";
import NotesHideIcon from "../../assets/svgs/notesHide.svg";
import NotesHideHoverIcon from "../../assets/svgs/notesHideHover.svg";
import NotesHideFocusIcon from "../../assets/svgs/notesHideFocus.svg";
import NotesHideDisabledIcon from "../../assets/svgs/notesHideDisabled.svg";

function parseTime24h(str) {
  const m = moment(str, ["HH:mm", "HH:mm:ss"], true);
  return m.isValid() ? m : null;
}
function formatTime24h(m) {
  return m.format("HH:mm");
}

function getWeekdayIndex1to7(dateStr) {
  const m = moment(dateStr, "YYYY-MM-DD", true);
  if (!m.isValid()) return null;

  // moment().isoWeekday(): Monday=1 ... Sunday=7
  return m.isoWeekday();
}

function parseConfigJson(raw) {
  if (raw == null) return null;

  if (Array.isArray(raw)) {
    return raw.length > 0 ? raw : null;
  }

  if (typeof raw === "object") {
    return Object.keys(raw).length > 0 ? raw : null;
  }

  if (typeof raw !== "string") return null;

  try {
    let parsed = JSON.parse(raw);

    if (typeof parsed === "string") parsed = JSON.parse(parsed);

    if (Array.isArray(parsed)) return parsed.length > 0 ? parsed : null;

    if (parsed && typeof parsed === "object") {
      return Object.keys(parsed).length > 0 ? parsed : null;
    }

    return null;
  } catch {
    return null;
  }
}

const TaskCreationModal = ({
  isOpen,
  onClose,
  taskData,
  onSuccess,
  defaultDate,
  empleado,
  isMobile,
  proyectosTodos,
}) => {
  const [tasks, setTasks] = useState([]);
  const [subtasks, setSubtasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectSearch, setProjectSearch] = useState("");

  const [isDateFocused, setIsDateFocused] = useState(false);
  const [isStartTimeFocused, setIsStartTimeFocused] = useState(false);
  const [isEndTimeFocused, setIsEndTimeFocused] = useState(false);
  const [isTaskFocused, setIsTaskFocused] = useState(false);
  const [isSubtaskFocused, setIsSubtaskFocused] = useState(false);
  const [isProjectFocused, setIsProjectFocused] = useState(false);
  const [isDurationFocused, setIsDurationFocused] = useState(false);
  const [isDescriptionFocused, setIsDescriptionFocused] = useState(false);
  const [isNotesFocused, setIsNotesFocused] = useState(false);

  const [repeatEvery, setRepeatEvery] = useState("1");
  const [repeatUnit, setRepeatUnit] = useState("Día");

  const [selectedDays, setSelectedDays] = useState([]);

  const [monthPick, setMonthPick] = useState("day");

  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [notesHover, setNotesHover] = useState(false);

  const [showConfirmEdit, setShowConfirmEdit] = useState(false);
  const [editScope, setEditScope] = useState("single"); // "single" | "all"
  const pendingSubmitRef = useRef(null); // ya lo tienes

  const [modalType, setModalType] = useState(null);

  const todayStr = moment().format("YYYY-MM-DD");
  const updatingDurationRef = useRef(false);
  const updatingEndRef = useRef(false);
  const manualStartChangeRef = useRef(false);

  const taskSnapshotRef = useRef(null);
  const hasInitializedRef = useRef(false);

  const initialPeriodicityRef = useRef({
    repeatEvery: "1",
    repeatUnit: "Día",
    selectedDays: [],
    monthPick: "day",
  });

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    trigger,
    getValues,
    setError,
    clearErrors,
    formState: { errors, isSubmitting, submitCount, isDirty, dirtyFields },
  } = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      selectedTask: "",
      selectedSubtask: "",
      selectedProject: "",
      selectedDate: defaultDate || new Date().toISOString().split("T")[0],
      hours: "",
      minutes: "",
      anytime: true,
      startTime: "",
      endTime: "",
      description: "",
      notes: "",
      repetition: "none",
      endMode: "never",
      endDate: "",
    },
  });

  const showErrors = submitCount > 0;
  const selectedTask = useWatch({ control, name: "selectedTask" });
  const anytime = useWatch({ control, name: "anytime" });
  const startTime = useWatch({ control, name: "startTime" });
  const endTime = useWatch({ control, name: "endTime" });
  const hours = useWatch({ control, name: "hours" });
  const minutes = useWatch({ control, name: "minutes" });
  const repetition = useWatch({ control, name: "repetition" });
  const selectedDate = useWatch({ control, name: "selectedDate" });
  const endMode = useWatch({ control, name: "endMode" });
  const endDate = useWatch({ control, name: "endDate" });
  const notes = useWatch({ control, name: "notes" });
  const isCreating = !taskData;
  const shouldDisableNotes = isCreating && repetition !== "none";
  const lastEditedRef = useRef(null);


  const isSalidasTask = selectedTask !== "Otra" && Number(selectedTask) === 3;

  const durationMessage = showErrors
    ? (errors.hours?.message || errors.minutes?.message || "")
    : "";

  const hStr = String(hours ?? "").trim();
  const mStr = String(minutes ?? "").trim();

  const hasH = hStr !== "";
  const hasM = mStr !== "";

  const hNum = hasH ? Number(hStr) : NaN;
  const mNum = hasM ? Number(mStr) : NaN;

  const bothZero = hasH && hasM && Number.isFinite(hNum) && Number.isFinite(mNum) && hNum === 0 && mNum === 0;

  const hoursHasError = showErrors && !!errors.hours;
  const minutesHasError = showErrors && !!errors.minutes;

  const hoursError =
    bothZero ||
    hoursHasError ||
    (hasH && !hasM && !!durationMessage);

  const minutesError =
    bothZero ||
    minutesHasError ||
    (!hasH && hasM && !!durationMessage);

  const oldDateRef = useRef(null);



  useEffect(() => {
    if (!isOpen) return;
    if (taskData) {
      oldDateRef.current =
        taskData?.Fecha ||
        taskData?.date ||
        taskData?.selectedDate ||
        "";
    } else {
      oldDateRef.current = null;
    }
  }, [isOpen, taskData]);

  // useEffect(() => {
  //   if (isOpen) {
  //     console.log("taskData en edición:", taskData);
  //     console.log("taskData.config_json:", taskData?.config_json);
  //   }
  // }, [isOpen, taskData]);

  useEffect(() => {
    const load = async () => {
      try {
        if (!empleado) return;
        const data = await getDailyByEmployee({ empleado });

        if (data?.Tareas && data?.Proyectos) {
          setTasks(data.Tareas);
          setProjects(data.Proyectos);
        } else {
          setTasks([]);
          setProjects([]);
        }
      } catch (error) {
        console.error("Error al cargar datos:", error);
        setTasks([]);
        setProjects([]);
      }
    };

    load();
  }, [empleado]);

  useEffect(() => {
    if (!isOpen) return;

    const hasExistingNotes = String(taskData?.Notas || "").trim() !== "";
    setShowNotes(hasExistingNotes);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    if (repetition === "custom") {
      const currentEndMode = getValues("endMode");
      if (!currentEndMode) setValue("endMode", "never", { shouldDirty: false });
    }
  }, [isOpen, repetition, setValue, getValues]);


  useEffect(() => {
    if (!isOpen) {
      taskSnapshotRef.current = null;
      hasInitializedRef.current = false;
      setProjectSearch("");
      setSubtasks([]);
      return;
    }

    if (!taskSnapshotRef.current || taskSnapshotRef.current?.id !== taskData?.id) {
      taskSnapshotRef.current = taskData ? { ...taskData } : null;
      hasInitializedRef.current = false;
    }

    if (isOpen && !taskData) {
      reset({
        selectedTask: "",
        selectedSubtask: "",
        selectedProject: "",
        selectedDate: defaultDate || new Date().toISOString().split("T")[0],
        hours: "",
        minutes: "",
        anytime: true,
        startTime: "",
        endTime: "",
        description: "",
        notes: "",
        repetition: "none",
        endMode: "never",
        endDate: "",
      });
      setRepeatEvery("1");
      setRepeatUnit("Día");
      setSelectedDays([]);
      setMonthPick("day");
      initialPeriodicityRef.current = {
        repeatEvery: "1",
        repeatUnit: "Día",
        selectedDays: [],
        monthPick: "day",
      };
      setSubtasks([]);
      setProjectSearch("");
      hasInitializedRef.current = true;
    }
  }, [isOpen, taskData, defaultDate, reset]);

  useEffect(() => {
    if (!isOpen) return;
    if (hasInitializedRef.current) return;
    if (!tasks.length || !projects.length) return;

    const td = taskSnapshotRef.current;
    if (!td) return;

    let repetitionValue = "none";
    let endModeValue = "never";
    let endDateValue = "";

    let repeatEveryValue = "1";
    let repeatUnitValue = "Día";
    let selectedDaysValue = [];
    let monthPickValue = "day";

    const cfg = parseConfigJson(td?.config_json ?? td?.configJson ?? td?.Config_json ?? td?.ConfigJson);

    if (!cfg || typeof cfg !== "object" || Array.isArray(cfg) || !cfg.type) {
      repetitionValue = "none";
    } else {
      const type = cfg.type;
    }

    const backendEnd =
      td?.endDate ||
      td?.end_date ||
      cfg?.endDate ||
      "";

    if (backendEnd) {
      endModeValue = "date";
      endDateValue = backendEnd;
    }

    if (cfg && typeof cfg === "object") {
      const type = cfg.type;

      // ----- NO CUSTOM
      if (type === "daily" && !cfg.interval) {
        repetitionValue = "daily";

      } else if (type === "weekly" && !cfg.interval && Array.isArray(cfg.day) && cfg.day.length === 1) {
        repetitionValue = "weekly";

      } else if (type === "weekly" && !cfg.interval && Array.isArray(cfg.day) && cfg.day.length === 5) {
        repetitionValue = "weekdays";

      } else if (type === "monthly-ordinal" && !cfg.interval) {
        repetitionValue = "monthly";

      } else {
        // ----- CUSTOM
        repetitionValue = "custom";

        if (cfg.interval) repeatEveryValue = String(cfg.interval);

        const numToLetter = { 1: "L", 2: "M", 3: "X", 4: "J", 5: "V", 6: "S", 7: "D" };

        if (type === "custom" && cfg.period_type === "days") {
          repeatUnitValue = "Día";
        }

        else if (type === "custom" && cfg.period_type === "weeks") {
          repeatUnitValue = "Semana";

          if (Array.isArray(cfg.week_days)) {
            selectedDaysValue = cfg.week_days.map((n) => numToLetter[n]).filter(Boolean);
          } else if (Array.isArray(cfg.day)) {
            selectedDaysValue = cfg.day.map((n) => numToLetter[n]).filter(Boolean);
          }
        }

        else if (type === "monthly") {
          repeatUnitValue = "Mes";
          monthPickValue = "day";
        }

        else if (type === "monthly-ordinal") {
          repeatUnitValue = "Mes";
          monthPickValue = "weekday";
        }

        else if (type === "weekly") {
          repeatUnitValue = "Semana";

          if (Array.isArray(cfg.week_days)) {
            selectedDaysValue = cfg.week_days.map((n) => numToLetter[n]).filter(Boolean);
          } else if (Array.isArray(cfg.day)) {
            selectedDaysValue = cfg.day.map((n) => numToLetter[n]).filter(Boolean);
          }
        }
      }
    }

    setRepeatEvery(repeatEveryValue);
    setRepeatUnit(repeatUnitValue);
    setSelectedDays(selectedDaysValue);
    setMonthPick(monthPickValue);
    initialPeriodicityRef.current = {
      repeatEvery: repeatEveryValue,
      repeatUnit: repeatUnitValue,
      selectedDays: [...selectedDaysValue].sort(),
      monthPick: monthPickValue,
    };

    const date =
      td.date ||
      defaultDate ||
      new Date().toISOString().split("T")[0];

    const taskHours = Math.floor((td.Minutos || 0) / 60);
    const taskMinutes = (td.Minutos || 0) % 60;

    let selectedTaskValue = "";
    let selectedSubtaskValue = "";

    if (td.Tarea === "Otra") {
      selectedTaskValue = "Otra";
      selectedSubtaskValue = td.Subtarea || "";
      setSubtasks([]);
    } else {
      const foundTask = tasks.find((t) => t.tarea === td.Tarea);
      if (foundTask) {
        selectedTaskValue = foundTask.id.toString();
        setSubtasks(foundTask.subtareas || []);
        const foundSub = foundTask.subtareas?.find((s) => s.subtarea === td.Subtarea);
        selectedSubtaskValue = foundSub ? foundSub.id.toString() : "";
      }
    }

    let foundProject = projects.find((p) => p.Proyecto === td.Proyecto);
    if (!foundProject) {
      foundProject = (proyectosTodos || []).find((p) => p.Proyecto === td.Proyecto);
    }
    const selectedProjectValue = foundProject ? foundProject.id.toString() : "";

    let calculatedEndTime = "";

    if (td.Inicio && Number(td.Minutos) > 0) {
      const start = moment(td.Inicio, ["HH:mm", "HH:mm:ss"], true);
    
      if (start.isValid()) {
        calculatedEndTime = start.clone().add(Number(td.Minutos), "minutes").format("HH:mm");
      }
    }

    reset({
      selectedTask: selectedTaskValue,
      selectedSubtask: selectedSubtaskValue,
      selectedProject: selectedProjectValue,
      selectedDate: date,
      hours: String(taskHours),
      minutes: String(taskMinutes),
      anytime: td.Inicio ? false : true,
      startTime: td.Inicio || "",
      endTime: calculatedEndTime,
      description: td?.Descripcion || "",
      notes: td?.Notas || "",
      repetition: repetitionValue,
      endMode: endModeValue,
      endDate: endDateValue,
    });

    setProjectSearch("");
    hasInitializedRef.current = true;
  }, [isOpen, tasks, projects, proyectosTodos, defaultDate, reset]);

  useEffect(() => {
    if (!isOpen) return;

    if (isSalidasTask) {
      setValue("anytime", false, {
        shouldValidate: true,
        shouldDirty: false,
      });
    } else {
      setValue("anytime", true, {
        shouldValidate: true,
        shouldDirty: false,
      });
    }
  }, [isOpen, isSalidasTask, setValue]);

  useEffect(() => {
    if (anytime) return;
    if (lastEditedRef.current !== "timeRange") return;

    const start = parseTime24h(startTime);
    const end = parseTime24h(endTime);
    if (!start || !end) return;

    const diff = end.diff(start, "minutes");
    if (diff < 0) return;

    const newHours = String(Math.floor(diff / 60));
    const newMinutes = String(diff % 60);

    if (getValues("hours") !== newHours) {
      setValue("hours", newHours, { shouldValidate: true, shouldDirty: true });
    }

    if (getValues("minutes") !== newMinutes) {
      setValue("minutes", newMinutes, { shouldValidate: true, shouldDirty: true });
    }
  }, [startTime, endTime, anytime, setValue, getValues]);

  useEffect(() => {
    if (!isOpen) return;
    if (!taskData && repetition !== "none") {
      setValue("notes", "", { shouldDirty: true, shouldValidate: false });
    }
  }, [isOpen, repetition, taskData, setValue]);

  useEffect(() => {
    if (anytime) return;
    if (lastEditedRef.current !== "duration") return;

    const start = parseTime24h(startTime);
    if (!start) return;

    const totalMins = (Number(hours) || 0) * 60 + (Number(minutes) || 0);
    if (totalMins <= 0) return;

    const newEnd = formatTime24h(start.clone().add(totalMins, "minutes"));

    if (getValues("endTime") !== newEnd) {
      setValue("endTime", newEnd, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [hours, minutes, anytime, startTime, setValue, getValues]);

  useEffect(() => {
    if (anytime) return;
    if (lastEditedRef.current !== "endTimeFromDuration") return;

    const end = parseTime24h(endTime);
    if (!end) return;

    const totalMins =
      (Number(hours) || 0) * 60 +
      (Number(minutes) || 0);

    if (totalMins <= 0) return;

    const newStart = formatTime24h(end.clone().subtract(totalMins, "minutes"));

    if (getValues("startTime") !== newStart) {
      setValue("startTime", newStart, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [endTime, hours, minutes, anytime, setValue, getValues]);

  const normalize = (s) => String(s || "").toLowerCase().trim();
  const userProjectOptions = useMemo(
    () => projects.map((p) => ({ value: p.id.toString(), label: p.Proyecto, __source: "user" })),
    [projects]
  );
  const allProjectsOptions = useMemo(
    () => (proyectosTodos || []).map((p) => ({ value: p.id.toString(), label: p.Proyecto, __source: "all" })),
    [proyectosTodos]
  );

  const search = normalize(projectSearch);
  const useAll = search.length >= 3;

  const filteredAll = useAll
    ? allProjectsOptions.filter((opt) => normalize(opt.label).includes(search))
    : [];

  const projectOptionsToShow = useAll ? filteredAll : userProjectOptions;

  const repetitionOptions = useMemo(() => {
    const m = moment(selectedDate, "YYYY-MM-DD", true);
    const weekday = m.isValid() ? m.locale("es").format("dddd") : "día";

    // Según el día del mes (ej: 1-7 => 1º, 8-14 => 2º, etc...)
    const occurrence = m.isValid() ? Math.floor((m.date() - 1) / 7) + 1 : 1;
    const ordMap = {
      1: "primer",
      2: "segundo",
      3: "tercer",
      4: "cuarto",
      5: "quinto",
    };
    const ordinal = ordMap[occurrence] || "primer";

    return [
      { value: "none", label: "No se repite" },
      { value: "weekdays", label: "Todos los días laborables" },
      { value: "daily", label: "Cada día" },
      { value: "weekly", label: `Cada semana el ${weekday}` },
      { value: "monthly", label: `Cada mes el ${ordinal} ${weekday}` },
      { value: "custom", label: "Personalizar" },
    ];
  }, [selectedDate]);

  const monthLabels = useMemo(() => {
    const m = moment(selectedDate, "YYYY-MM-DD", true).locale("es");
    const dayNum = m.isValid() ? m.date() : 1;

    const weekday = m.isValid() ? m.format("dddd") : "lunes"; // "viernes"
    const occurrence = m.isValid() ? Math.floor((m.date() - 1) / 7) + 1 : 1; // 1..5

    const ordMap = { 1: "1º", 2: "2º", 3: "3º", 4: "4º", 5: "5º" };
    const ord = ordMap[occurrence] || "1º";

    return {
      day: `Día ${dayNum}`,
      weekday: `${ord} ${weekday}`,
    };
  }, [selectedDate]);

  const CustomDropdownIndicator = (props) => (
    <components.DropdownIndicator {...props}>
      <img src={LabelArrowIcon} alt="arrow" style={{ width: 12, height: 12 }} />
    </components.DropdownIndicator>
  );

  const periodicityFieldsChanged =
    repeatEvery !== initialPeriodicityRef.current.repeatEvery ||
    repeatUnit !== initialPeriodicityRef.current.repeatUnit ||
    monthPick !== initialPeriodicityRef.current.monthPick ||
    JSON.stringify([...selectedDays].sort()) !==
    JSON.stringify([...initialPeriodicityRef.current.selectedDays].sort());

  const nonNotesDirtyFields = Object.keys(dirtyFields || {}).filter(
    (key) => key !== "notes"
  );

  const dirtyFieldsWithoutAnytime = Object.keys(dirtyFields || {}).filter(
    (key) => key !== "anytime"
  );

  const onlyNotesChanged =
    !!taskData &&
    !!dirtyFields?.notes &&
    nonNotesDirtyFields.length === 0 &&
    !periodicityFieldsChanged;

  const formHasChanges =
    dirtyFieldsWithoutAnytime.length > 0 || periodicityFieldsChanged;

  const shouldConfirmClose = () => formHasChanges;

  const handleClose = () => {
    if (shouldConfirmClose()) {
      setShowCloseConfirm(true);
      return;
    }
    onClose?.();
  };

  const handleCancelEdit = () => {
    setShowConfirmEdit(false);
    pendingSubmitRef.current = null;
  };

  const handleConfirmEdit = async () => {
    const v = pendingSubmitRef.current;
    setShowConfirmEdit(false);
    if (!v) return;

    let apply_all = false;

    if (modalType === "periodicityChanged") apply_all = true;
    if (modalType === "scopeSelector") apply_all = editScope === "all";

    await doSave(v, apply_all);
    pendingSubmitRef.current = null;
  };

  const doSave = async (values, apply_all) => {
    const hoursNum = Number(values.hours) || 0;
    const minutesNum = Number(values.minutes) || 0;

    const finalTarea = values.selectedTask === "Otra" ? "Otra" : Number(values.selectedTask);
    const finalSubtarea =
      values.selectedTask === "Otra" ? values.selectedSubtask : Number(values.selectedSubtask || 0);

    let config_json = null;

    if (values.repetition === "none") config_json = [];

    const intervalRaw = Number(String(repeatEvery ?? "").trim());
    const safeInterval = Number.isFinite(intervalRaw) && intervalRaw > 0 ? intervalRaw : 1;

    if (values.repetition === "daily") {
      config_json = { type: "daily" };
    } else if (values.repetition === "weekly") {
      const day = getWeekdayIndex1to7(values.selectedDate);
      config_json = { type: "weekly", day: [day] };
    } else if (values.repetition === "monthly") {
      config_json = { type: "monthly-ordinal", date: values.selectedDate };
    } else if (values.repetition === "weekdays") {
      config_json = { type: "weekly", day: [1, 2, 3, 4, 5] };
    } else if (values.repetition === "custom" && repeatUnit === "Día") {
      config_json = { type: "custom", period_type: "days", interval: safeInterval };
    } else if (values.repetition === "custom" && repeatUnit === "Semana") {
      const weekDayMap = { L: 1, M: 2, X: 3, J: 4, V: 5, S: 6, D: 7 };
      const weekDays = selectedDays
        .map((d) => weekDayMap[d])
        .filter(Boolean)
        .sort((a, b) => a - b);

      config_json = { type: "custom", period_type: "weeks", week_days: weekDays, interval: safeInterval };
    } else if (values.repetition === "custom" && repeatUnit === "Mes") {
      const m = moment(values.selectedDate, "YYYY-MM-DD", true);

      if (monthPick === "day") {
        const dayOfMonth = m.isValid() ? m.date() : null;
        config_json = { type: "monthly", day: dayOfMonth ? [dayOfMonth] : [], interval: safeInterval };
      } else {
        config_json = { type: "monthly-ordinal", date: values.selectedDate, interval: safeInterval };
      }
    }

    let finalEndDate = null;
    if (values.repetition === "custom" && values.endMode === "date" && values.endDate) {
      const end = moment(values.endDate, "YYYY-MM-DD", true);
      const start = moment(values.selectedDate, "YYYY-MM-DD", true);
      if (end.isValid() && end.isAfter(start)) finalEndDate = values.endDate;
    }

    const isProjectionValue = !!taskData?.is_projection;

    const oldDate = oldDateRef.current;
    const newDate = values.selectedDate;
    const includeOldDate = isProjectionValue && !!oldDate && newDate !== oldDate;

    const payload = {
      taskId: taskData ? taskData.id : null,
      userId: Number(localStorage.getItem("empleado")),
      Tarea: finalTarea,
      Subtarea: finalSubtarea,
      Proyecto: Number(values.selectedProject),
      Fecha: values.selectedDate,
      Minutos: hoursNum * 60 + minutesNum,
      Inicio: values.anytime ? "" : values.startTime,
      Descripcion: values.description,
      Notas: values.notes,
      endDate: finalEndDate,
      config_json,
      is_projection: isProjectionValue,
      ...(includeOldDate ? { oldDate } : {}),
      ...(typeof apply_all === "boolean" ? { apply_all } : {}),
    };

    try {
      const data = await saveTask(payload);
      const ok = data?.OK === 1 || data?.OK === "1" || data?.OK === true;
      if (!ok) return;

      onSuccess?.(taskData ? "Tarea editada correctamente" : "Tarea creada correctamente");
      onClose?.();
    } catch (error) {
      console.error("Error guardando tarea:", error);
    }
  };

  const onSubmit = async (values) => {
    if (values.repetition === "custom" && repeatUnit === "Semana" && selectedDays.length === 0) {
      setError("selectedDays", {
        type: "manual",
        message: "Selecciona al menos un día de la semana",
      });
      return;
    }

    clearErrors("selectedDays");

    if (!formHasChanges) {
      onClose?.();
      return;
    }

    if (!taskData) {
      await doSave(values);
      return;
    }

    if (onlyNotesChanged) {
      pendingSubmitRef.current = values;
      setEditScope("single");
      setModalType("normal");
      setShowConfirmEdit(true);
      return;
    }

    const originalDate = oldDateRef.current || "";
    const dateChanged = values.selectedDate !== originalDate;

    if (dateChanged) {
      pendingSubmitRef.current = values;
      setEditScope("single");
      setModalType("normal");
      setShowConfirmEdit(true);
      return;
    }

    const previewConfig = buildConfigPreview(values);
    const periodicityChanged = hasConfigChanged(previewConfig);

    const wasPeriodic = hasMeaningfulConfig(initialConfigRef.current);
    const willBePeriodic = hasMeaningfulConfig(previewConfig);
    const becamePeriodic = !wasPeriodic && willBePeriodic;

    pendingSubmitRef.current = values;

    if (becamePeriodic) {
      setEditScope("all");
      setModalType("periodicityChanged");
      setShowConfirmEdit(true);
      return;
    }

    if (!wasPeriodic) {
      setEditScope("single");
      setModalType("normal");
      setShowConfirmEdit(true);
      return;
    }

    if (periodicityChanged) {
      setEditScope("all");
      setModalType("periodicityChanged");
      setShowConfirmEdit(true);
      return;
    }

    setEditScope("single");
    setModalType("scopeSelector");
    setShowConfirmEdit(true);
  };

  const buildConfigPreview = (values) => {
    let config_json = null;

    const intervalRaw = Number(String(repeatEvery ?? "").trim());
    const safeInterval = Number.isFinite(intervalRaw) && intervalRaw > 0 ? intervalRaw : 1;

    if (values.repetition === "none") return [];

    if (values.repetition === "daily") return { type: "daily" };

    if (values.repetition === "weekly") {
      const day = getWeekdayIndex1to7(values.selectedDate);
      return { type: "weekly", day: [day] };
    }

    if (values.repetition === "monthly") {
      return { type: "monthly-ordinal", date: values.selectedDate };
    }

    if (values.repetition === "weekdays") {
      return { type: "weekly", day: [1, 2, 3, 4, 5] };
    }

    if (values.repetition === "custom" && repeatUnit === "Día") {
      return { type: "custom", period_type: "days", interval: safeInterval };
    }

    if (values.repetition === "custom" && repeatUnit === "Semana") {
      const weekDayMap = { L: 1, M: 2, X: 3, J: 4, V: 5, S: 6, D: 7 };
      const weekDays = selectedDays
        .map((d) => weekDayMap[d])
        .filter(Boolean)
        .sort((a, b) => a - b);

      return { type: "custom", period_type: "weeks", week_days: weekDays, interval: safeInterval };
    }

    if (values.repetition === "custom" && repeatUnit === "Mes") {
      const m = moment(values.selectedDate, "YYYY-MM-DD", true);

      if (monthPick === "day") {
        const dayOfMonth = m.isValid() ? m.date() : null;
        return { type: "monthly", day: dayOfMonth ? [dayOfMonth] : [], interval: safeInterval };
      }

      return { type: "monthly-ordinal", date: values.selectedDate, interval: safeInterval };
    }

    return config_json;
  };

  const validateHours = (v) => {
    const hStr = String(v ?? "").trim();
    const mStr = String(getValues("minutes") ?? "").trim();

    const hasH = hStr !== "";
    const hasM = mStr !== "";

    if (!hasH && !hasM) return "Indica una duración válida en horas o minutos";

    if (!hasH) return true;

    const h = Number(hStr);
    if (!Number.isFinite(h) || h < 0) return "Horas inválidas";
    if (h > 23) return "Máx. 23h";

    if (!hasM && h === 0) return "Indica una duración de al menos 1 minuto";

    const m = hasM ? Number(mStr) : 0;
    const total = (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
    if (hasM && total <= 0) return "Indica una duración de al menos 1 minuto";

    return true;
  };


  const validateMinutes = (v) => {
    const mStr = String(v ?? "").trim();
    const hStr = String(getValues("hours") ?? "").trim();

    const hasM = mStr !== "";
    const hasH = hStr !== "";

    if (!hasH && !hasM) return "Indica la duración (horas o minutos)";

    if (!hasM) return true;

    const m = Number(mStr);
    if (!Number.isFinite(m) || m < 0) return "Minutos inválidos";
    if (m > 59) return "Máx. 59min";

    if (!hasH && m === 0) return "Indica una duración de al menos 1 minuto";

    const h = hasH ? Number(hStr) : 0;
    const safeH = Number.isFinite(h) ? h : 0;
    const total = safeH * 60 + m;

    if (hasH && total <= 0) return "Indica una duración de al menos 1 minuto";

    return true;
  };

  const FormError = ({ message }) => (
    <p className={styles.errorText}>
      <img src={WarningFormIcon} alt="Error" className={styles.errorIcon} />
      <span>{message}</span>
    </p>
  );

  const cfgEdit = parseConfigJson(taskData?.config_json);

  const initialConfigRef = useRef(null);

  useEffect(() => {
    if (isOpen && taskData) {
      initialConfigRef.current = parseConfigJson(taskData?.config_json);
    }
  }, [isOpen, taskData]);

  const hasConfigChanged = (newConfig) => {
    const oldConfig = initialConfigRef.current;

    return JSON.stringify(oldConfig) !== JSON.stringify(newConfig);
  };

  const hasMeaningfulConfig = (cfg) => {
    if (cfg == null) return false;
    if (Array.isArray(cfg)) return cfg.length > 0;
    if (typeof cfg === "object") return Object.keys(cfg).length > 0;
    return false;
  };

  const isRecurringEdit =
    !!taskData &&
    cfgEdit != null &&
    (Array.isArray(cfgEdit) ? cfgEdit.length > 0 : typeof cfgEdit === "object");

  const shouldShowEditScopeSelector = isRecurringEdit;

  const lockConfigFields =
    !!taskData && hasMeaningfulConfig(cfgEdit);

  const hasExistingPeriodicity =
    !!taskData && hasMeaningfulConfig(cfgEdit);

  const originalDate = oldDateRef.current || "";

  const dateChanged =
    hasExistingPeriodicity && selectedDate !== originalDate;

  const currentPreviewConfig = dateChanged
    ? initialConfigRef.current
    : buildConfigPreview({
        repetition,
        selectedDate,
        endMode,
        endDate,
      });

  const previewHasPeriodicity = hasMeaningfulConfig(currentPreviewConfig);

  const initialConfigReady = initialConfigRef.current !== null;

  const repetitionChanged =
    !!taskData &&
    initialConfigReady &&
    !dateChanged &&
    hasConfigChanged(currentPreviewConfig);

  const normalTaskBecamePeriodic =
    !!taskData &&
    !hasExistingPeriodicity &&
    previewHasPeriodicity;

  const lockNotesField = repetitionChanged || normalTaskBecamePeriodic;

  const notesChanged =
    hasExistingPeriodicity &&
    String(notes || "") !== String(taskData?.Notas || "");

  const lockRepetitionFields = dateChanged || notesChanged;
  const lockDateField = repetitionChanged;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      shouldCloseOnOverlayClick={true}
      className={styles.modalContent}
      overlayClassName={styles.modalOverlay}
    >
      <button onClick={handleClose} className={styles.closeButton} aria-label="Cerrar modal">
        &times;
      </button>

      <p className={styles.formTitle}>{taskData ? "Editar tarea" : "Crear nueva tarea"}</p>
      <p className={styles.formSubtitle}>
        Completa el formulario para {taskData ? "editar esta tarea" : "crear una nueva tarea"}.
      </p>

      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        {/* Tarea */}
        <div className={styles.formGroup}>
          <label>
            <p className={`${styles.labelText} ${isTaskFocused ? styles.labelFocused : ""}`}>
              Tarea <span className={styles.required}>*</span>
            </p>
          </label>

          <Controller
            control={control}
            name="selectedTask"
            rules={{ required: true }}
            render={({ field }) => (
              <Select
                className={`${styles.select} ${showErrors && errors.selectedTask ? styles.selectError : ""}`}
                classNamePrefix="custom-select"
                components={{ DropdownIndicator: CustomDropdownIndicator }}
                options={[
                  ...tasks.map((t) => ({ value: t.id.toString(), label: t.tarea })),
                  { value: "Otra", label: "Otra" },
                ]}
                value={
                  field.value
                    ? {
                      value: field.value,
                      label:
                        field.value === "Otra"
                          ? "Otra"
                          : tasks.find((t) => t.id.toString() === field.value)?.tarea || "",
                    }
                    : null
                }
                onChange={(selectedOption) => {
                  const value = selectedOption?.value || "";

                  field.onChange(value);
                  setValue("selectedSubtask", "", { shouldValidate: true, shouldDirty: true });

                  if (value === "Otra") {
                    setSubtasks([]);
                  } else {
                    const numericId = Number(value);
                    const selectedTaskObject = tasks.find((t) => t.id === numericId);
                    setSubtasks(selectedTaskObject?.subtareas || []);
                  }

                  if (value !== "Otra" && Number(value) === 3) {
                    setValue("anytime", false, { shouldValidate: true, shouldDirty: false });
                  }

                  setIsTaskFocused(false);
                }}

                placeholder="Selecciona una tarea"
                isSearchable={false}
                isDisabled={lockConfigFields}
                onFocus={() => setIsTaskFocused(true)}
                onBlur={() => setIsTaskFocused(false)}
              />
            )}
          />
          {showErrors && errors.selectedTask && <FormError message="Selecciona una tarea para registrar el trabajo" />}
        </div>

        {/* Subtarea */}
        <div className={styles.formGroup}>
          {selectedTask !== "Otra" && (
            <label>
              <p className={`${styles.labelText} ${isSubtaskFocused ? styles.labelFocused : ""}`}>
                Subtarea <span className={styles.required}>*</span>
              </p>
            </label>
          )}

          {selectedTask === "Otra" ? (
            <>
              <input
                type="text"
                placeholder="Describe brevemente la tarea"
                className={`${styles.subtaskOtherInput} ${showErrors && errors.selectedSubtask ? styles.inputErrorUnderline : ""
                  }`}
                {...register("selectedSubtask", {
                  required: "Indica de qué tarea se trata",
                })}
                disabled={lockConfigFields}
                onFocus={() => setIsSubtaskFocused(true)}
                onBlur={() => setIsSubtaskFocused(false)}
              />

              {showErrors && errors.selectedSubtask && (
                <FormError message={errors.selectedSubtask.message || "Indica de qué tarea de trata"} />
              )}
            </>
          ) : (
            <>
              <Controller
                control={control}
                name="selectedSubtask"
                rules={{ required: "Selecciona una subtarea asociada a la tarea seleccionada" }}
                render={({ field }) => (
                  <Select
                    className={`${styles.select} ${showErrors && errors.selectedSubtask ? styles.selectError : ""
                      }`}
                    classNamePrefix="custom-select"
                    components={{ DropdownIndicator: CustomDropdownIndicator }}
                    options={
                      subtasks.length > 0
                        ? subtasks.map((sub) => ({
                          value: sub.id.toString(),
                          label: sub.subtarea,
                        }))
                        : []
                    }
                    value={
                      field.value
                        ? {
                          value: field.value,
                          label: subtasks.find((s) => s.id.toString() === field.value)?.subtarea || "",
                        }
                        : null
                    }
                    onChange={(opt) => {
                      const val = opt?.value || "";
                      field.onChange(val);
                      setValue("selectedSubtask", val, { shouldValidate: true, shouldDirty: true });
                      setIsSubtaskFocused(false);
                    }}
                    placeholder="Selecciona una subtarea"
                    isDisabled={lockConfigFields || subtasks.length === 0}
                    isSearchable={false}
                    onFocus={() => setIsSubtaskFocused(true)}
                    onBlur={() => setIsSubtaskFocused(false)}
                  />
                )}
              />

              {showErrors && errors.selectedSubtask && (
                <FormError message={errors.selectedSubtask.message} />
              )}
            </>
          )}
        </div>


        {/* Proyecto */}
        <div className={styles.formGroup}>
          <label>
            <p className={`${styles.labelText} ${isProjectFocused ? styles.labelFocused : ""}`}>
              Proyecto <span className={styles.required}>*</span>
            </p>
          </label>

          <Controller
            control={control}
            name="selectedProject"
            rules={{ required: true }}
            render={({ field }) => {
              const selectedOption =
                field.value
                  ? [...userProjectOptions, ...allProjectsOptions].find((o) => o.value === field.value) || null
                  : null;

              return (
                <Select
                  className={`${styles.select} ${showErrors && errors.selectedProject ? styles.selectError : ""}`}
                  classNamePrefix="custom-select"
                  components={{ DropdownIndicator: CustomDropdownIndicator }}
                  options={projectOptionsToShow}
                  value={selectedOption}
                  onChange={(opt) => {
                    field.onChange(opt?.value || "");
                    setValue("selectedProject", opt?.value || "", {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    setIsProjectFocused(false);
                  }}
                  placeholder={useAll ? "Buscando en todos los proyectos..." : "Selecciona o busca un proyecto"}
                  isSearchable={true}
                  inputValue={projectSearch}
                  onInputChange={(val, meta) => {
                    if (meta.action === "input-change") setProjectSearch(val);
                    if (meta.action === "set-value") setProjectSearch("");
                  }}
                  filterOption={null}
                  noOptionsMessage={() => {
                    if (useAll) {
                      return (
                        <div className={styles.noOptionsContainer}>
                          <div className={styles.noOptionsTitle}>
                            No se han encontrado proyectos con “{projectSearch}”
                          </div>
                          <div className={styles.noOptionsSubtitle}>
                            Prueba con otro término o selecciona uno de tus proyectos
                          </div>
                        </div>
                      );
                    }
                    return "No tienes proyectos asignados";
                  }}
                  isDisabled={lockConfigFields}
                  onFocus={() => setIsProjectFocused(true)}
                  onBlur={() => setIsProjectFocused(false)}
                />
              );
            }}
          />

          {showErrors && errors.selectedProject && (
            <FormError message="Indica el proyecto al que pertenece este trabajo" />
          )}
        </div>

        {/* Fecha + Duración */}
        {isMobile ? (
          <>
            {/* Fecha */}
            <div
              className={`${styles.formGroup} ${isDateFocused ? styles.focused : ""}`}
              onFocus={() => setIsDateFocused(true)}
              onBlur={() => setIsDateFocused(false)}
            >
              <label>
                <p className={`${styles.labelText} ${isDateFocused ? styles.labelFocused : ""}`}>
                  Fecha <span className={styles.required}>*</span>
                </p>
              </label>

              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div className={styles.iconContainer}>
                  <img src={CalendarIcon} alt="Calendario" />
                </div>

                <input
                  type="date"
                  className={`${styles.dateInput} ${showErrors && errors.selectedDate ? styles.inputErrorUnderline : ""}`}
                  {...register("selectedDate", { required: true })}
                  disabled={lockDateField}
                  onFocus={() => setIsDateFocused(true)}
                  onBlur={() => setIsDateFocused(false)}
                />
              </div>

              {showErrors && errors.selectedDate && <FormError message="Indica la fecha en la que se realizará el trabajo" />}
            </div>

            {/* Duración */}
            <div className={styles.formGroup}>
              <label>
                <p className={`${styles.labelText} ${isDurationFocused ? styles.labelFocused : ""}`}>
                  Duración <span className={styles.required}>*</span>
                </p>
              </label>

              <div
                className={`${styles.durationContainer}`}
              >
                <div className={styles.iconContainer}>
                  <img src={TimeIcon} alt="Tiempo" />
                </div>

                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={2}
                  placeholder="-- h"
                  className={`${styles.timeInput} ${hoursError ? styles.inputErrorUnderline : ""}`}
                  {...register("hours", { validate: validateHours })}
                  onChange={(e) => {
                    lastEditedRef.current = "duration";
                    const val = e.target.value.replace(/\D/g, "");
                    setValue("hours", val, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    trigger(["hours", "minutes", "endTime"]);

                  }}
                  onFocus={() => setIsDurationFocused(true)}
                  onBlur={() => setIsDurationFocused(false)}
                />

                <span className={styles.separator}>:</span>

                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={2}
                  placeholder="-- min"
                  className={`${styles.timeInput} ${minutesError ? styles.inputErrorUnderline : ""}`}
                  {...register("minutes", { validate: validateMinutes })}
                  onChange={(e) => {
                    lastEditedRef.current = "duration";
                    const val = e.target.value.replace(/\D/g, "");
                    setValue("minutes", val, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    trigger(["hours", "minutes", "endTime"]);
                  }}
                  onFocus={() => setIsDurationFocused(true)}
                  onBlur={() => setIsDurationFocused(false)}
                />
              </div>

              {durationMessage && <FormError message={durationMessage} />}
            </div>
          </>
        ) : (
          <div className={styles.formGroup}>
            <div className={styles.row}>
              {/* Fecha */}
              <div
                className={`${styles.formGroup} ${isDateFocused ? styles.focused : ""}`}
                onFocus={() => setIsDateFocused(true)}
                onBlur={() => setIsDateFocused(false)}
              >
                <label>
                  <p className={`${styles.labelText} ${isDateFocused ? styles.labelFocused : ""}`}>
                    Fecha <span className={styles.required}>*</span>
                  </p>
                </label>

                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <div className={styles.iconContainer}>
                    <img src={CalendarIcon} alt="Calendario" />
                  </div>
                  <input
                    type="date"
                    className={`${styles.dateInput} ${errors.selectedDate ? styles.inputErrorUnderline : ""}`}
                    {...register("selectedDate", { required: true })}
                    disabled={lockDateField}
                    onFocus={() => setIsDateFocused(true)}
                    onBlur={() => setIsDateFocused(false)}
                  />
                </div>
                {errors.selectedDate && <FormError message="Indica la fecha en la que se realizará el trabajo" />}
              </div>

              {/* Duración */}
              <div className={styles.formGroup}>
                <label>
                  <p className={`${styles.labelText} ${isDurationFocused ? styles.labelFocused : ""}`}>
                    Duración <span className={styles.required}>*</span>
                  </p>
                </label>

                <div
                  className={`${styles.durationContainer}`}
                >
                  <div className={styles.iconContainer}>
                    <img src={TimeIcon} alt="Tiempo" />
                  </div>

                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={2}
                    placeholder="-- h"
                    className={`${styles.timeInput} ${hoursError ? styles.inputErrorUnderline : ""}`}
                    {...register("hours", { validate: validateHours })}
                    onChange={(e) => {
                      lastEditedRef.current = "duration";
                      const val = e.target.value.replace(/\D/g, "");
                      setValue("hours", val, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                      trigger(["hours", "minutes", "endTime"]);
                    }}
                    onFocus={() => setIsDurationFocused(true)}
                    onBlur={() => setIsDurationFocused(false)}
                  />

                  <span className={styles.separator}>:</span>

                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={2}
                    placeholder="-- min"
                    className={`${styles.timeInput} ${minutesError ? styles.inputErrorUnderline : ""}`}
                    {...register("minutes", { validate: validateMinutes })}
                    onChange={(e) => {
                      lastEditedRef.current = "duration";
                      const val = e.target.value.replace(/\D/g, "");
                      setValue("minutes", val, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                      trigger(["hours", "minutes", "endTime"]);
                    }}
                    onFocus={() => setIsDurationFocused(true)}
                    onBlur={() => setIsDurationFocused(false)}
                  />
                </div>
                {durationMessage && <FormError message={durationMessage} />}
              </div>
            </div>
          </div>
        )}

        {/* Checkbox anytime*/}
        {!isSalidasTask && (
          <div className={styles.customCheckboxWrapper}>
            <input
              type="checkbox"
              id="anytimeCheckbox"
              checked={!!anytime}
              onChange={(e) =>
                setValue("anytime", e.target.checked, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              className={styles.hiddenCheckbox}
            />
            <label
              htmlFor="anytimeCheckbox"
              className={`${styles.customCheckbox} ${isMobile ? styles.mobile : ""}`}
            />
            <p className={styles.anytime}>En cualquier momento del día</p>
          </div>
        )}

        {/* Hora */}
        {(isSalidasTask || !anytime) && (
          <div className={styles.formGroup}>
            <label>
              <p className={`${styles.labelText} ${isStartTimeFocused ? styles.labelFocused : ""}`}>
                Hora <span className={styles.required}>*</span>
              </p>
            </label>

            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div className={styles.iconContainer}>
                <img src={ClockIcon} alt="Reloj" />
              </div>

              <div className={styles.timeRangeContainer}>
                <input
                  type="time"
                  className={`${styles.timeInput} ${isStartTimeFocused ? styles.focusedInput : ""} ${isMobile ? styles.mobileTimeInput : ""
                    } ${showErrors && errors.startTime ? styles.inputErrorUnderline : ""}`}
                  value={startTime || ""}
                  {...register("startTime", {
                    validate: (v) => {
                      if (!(isSalidasTask || !anytime)) return true;
                      return v ? true : "Indica la Franja Horaria en la que se realizará el trabajo";
                    },
                  })}
                  onChange={(e) => {
                    setValue("startTime", e.target.value, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  
                    const totalMins =
                      (Number(getValues("hours")) || 0) * 60 +
                      (Number(getValues("minutes")) || 0);
                  
                    lastEditedRef.current = totalMins > 0 ? "duration" : "timeRange";
                  
                    trigger(["startTime", "endTime", "hours", "minutes"]);
                    manualStartChangeRef.current = true;
                  }}

                  onFocus={() => setIsStartTimeFocused(true)}
                  onMouseDown={() => setIsStartTimeFocused(true)}
                  onBlur={() => setIsStartTimeFocused(false)}
                  step="900"
                />

                <span className={styles.separator}>-</span>

                <input
                  type="time"
                  className={`${styles.timeInput} ${isEndTimeFocused ? styles.focusedInput : ""} ${isMobile ? styles.mobileTimeInput : ""
                    } ${errors.endTime ? styles.inputErrorUnderline : ""}`}
                  value={endTime || ""}
                  {...register("endTime", {
                    validate: (v) => {
                      if (!(isSalidasTask || !anytime)) return true;
                      return v ? true : "Indica la Franja Horaria en la que se finalizará el trabajo";
                    },
                  })}
                  onChange={(e) => {
                    const value = e.target.value;

                    setValue("endTime", value, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  
                    lastEditedRef.current = "timeRange";
                  
                    trigger(["startTime", "endTime", "hours", "minutes"]);
                  }}
                  onFocus={() => setIsEndTimeFocused(true)}
                  onMouseDown={() => setIsEndTimeFocused(true)}
                  onBlur={() => setIsEndTimeFocused(false)}
                  step="900"
                />
              </div>
            </div>
            {showErrors && (errors.startTime || errors.endTime) && (
              <p className={styles.errorText}><img src={WarningFormIcon} alt="Error" className={styles.errorIcon} />{errors.startTime?.message || errors.endTime?.message}</p>
            )}
          </div>
        )}

        {/* Repetición */}
        <div className={styles.formGroup}>
          <label>
            <p className={`${styles.labelText} ${isProjectFocused ? "" : ""}`}>
              Repetición
            </p>
          </label>

          <Controller
            control={control}
            name="repetition"
            render={({ field }) => (
              <Select
                className={styles.select}
                classNamePrefix="custom-select"
                components={{ DropdownIndicator: CustomDropdownIndicator }}
                options={repetitionOptions}
                value={repetitionOptions.find((o) => o.value === field.value) || repetitionOptions[0]}
                onChange={(opt) => {
                  field.onChange(opt?.value || "none");
                  setValue("repetition", opt?.value || "none", {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
                placeholder="Selecciona una opción"
                isSearchable={false}
                isDisabled={lockRepetitionFields}              />
            )}
          />
        </div>

        {repetition === "custom" && (
          <div className={`${styles.customRepeatRow} ${isMobile ? styles.customRepeatRowMobile : ""}`}>
            <div className={styles.customRepeatLeft}>
              <p className={styles.labelText}>Repetir cada <span className={styles.required}>*</span></p>

              <div className={styles.customRepeatLeftBox}>
                <div className={styles.iconContainer}>
                  <img src={PeriodicityIcon} alt="Periodicidad" />
                </div>

                <input
                  className={styles.repeatEveryInput}
                  value={repeatEvery}
                  disabled={lockRepetitionFields}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "");
                    setRepeatEvery(v === "" ? "" : String(Math.min(Math.max(Number(v), 1), 99)));
                  }}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />

                <Select
                  className={`${styles.repeatUnitSelect}`}
                  classNamePrefix="custom-select"
                  components={{ DropdownIndicator: CustomDropdownIndicator }}
                  options={[
                    { value: "Día", label: "Día" },
                    { value: "Semana", label: "Semana" },
                    { value: "Mes", label: "Mes" },
                  ]}
                  value={{ value: repeatUnit, label: repeatUnit }}
                  onChange={(opt) => setRepeatUnit(opt?.value || "Semana")}
                  isSearchable={false}
                  isDisabled={lockRepetitionFields}
                />
              </div>
            </div>

            {repeatUnit !== "Día" && (
              <div className={styles.customRepeatRight}>
                {repeatUnit === "Semana" ? (
                  <>
                    <p className={styles.labelText}>Los días <span className={styles.required}>*</span></p>
                    <div className={styles.weekDaysRow}>
                      {["L", "M", "X", "J", "V", "S", "D"].map((d) => {
                        const active = selectedDays.includes(d);
                        return (
                          <button
                            key={d}
                            type="button"
                            disabled={lockRepetitionFields}
                            className={`${styles.dayCircle} ${active ? styles.dayCircleActive : ""}`}
                            onClick={() => {
                              if (lockRepetitionFields) return;

                              setSelectedDays((prev) => {
                                const next = prev.includes(d)
                                  ? prev.filter((x) => x !== d)
                                  : [...prev, d];

                                if (next.length > 0) {
                                  clearErrors("selectedDays");
                                }

                                return next;
                              });
                            }}
                          >
                            {d}
                          </button>
                        );
                      })}
                    </div>

                    {showErrors && errors.selectedDays && (
                      <FormError message={errors.selectedDays.message} />
                    )}
                  </>
                ) : (
                  <>
                    <p className={styles.labelText}>El <span className={styles.required}>*</span></p>

                    <div className={`${styles.monthToggle} ${styles.monthToggleLocked}`}>
                      <button
                        type="button"
                        disabled={lockRepetitionFields}
                        className={`${styles.monthToggleBtn} ${monthPick === "day" ? styles.monthToggleActive : ""}`}
                        onClick={() => {
                          if (lockRepetitionFields) return;
                          setMonthPick("day");
                        }}
                      >
                        {monthLabels.day}
                      </button>
                      
                      <button
                        type="button"
                        disabled={lockRepetitionFields}
                        className={`${styles.monthToggleBtn} ${monthPick === "weekday" ? styles.monthToggleActive : ""}`}
                        onClick={() => {
                          if (lockRepetitionFields) return;
                          setMonthPick("weekday");
                        }}
                      >
                        {monthLabels.weekday}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

          </div>
        )}

        {repetition === "custom" && (
          <>
            <div className={styles.formGroup}>
              <p className={styles.labelText}>Termina</p>

              <div className={styles.endsRow}>
                <label className={styles.endsOption}>
                  <input
                    type="radio"
                    value="never"
                    {...register("endMode")}
                    disabled={lockRepetitionFields}
                  />
                  <span className={styles.radioCustom} />
                  <span className={styles.endsText}>Nunca</span>
                </label>

                <div className={styles.endsRight}>
                  <label className={styles.endsOption}>
                    <input
                      type="radio"
                      value="date"
                      {...register("endMode")}
                      disabled={lockRepetitionFields}
                    />
                    <span className={styles.radioCustom} />
                    <span className={styles.endsText}>El</span>
                  </label>

                  <input
                    type="date"
                    min={todayStr}
                    className={`${styles.endsDate} ${endMode !== "date" ? styles.endsDateDisabled : ""}`}
                    {...register("endDate", {
                      validate: (value) => {
                        if (repetition !== "custom") return true;
                        if (endMode !== "date") return true;
                      
                        if (!value) return "Selecciona una fecha de fin";
                      
                        const end = moment(value, "YYYY-MM-DD", true);
                        if (!end.isValid()) return "Fecha inválida";
                      
                        const today = moment(todayStr, "YYYY-MM-DD", true);
                        const taskDate = moment(selectedDate, "YYYY-MM-DD", true);
                      
                        if (end.isBefore(today, "day")) {
                          return "La fecha de fin no puede ser anterior a hoy";
                        }
                      
                        if (end.isBefore(taskDate)) {
                          return "Debe ser igual o posterior a la fecha inicial";
                        }
                      
                        return true;
                      }
                    })}
                    readOnly={lockRepetitionFields || endMode !== "date"}
                    disabled={lockRepetitionFields}
                    onFocus={() => {
                      if (lockRepetitionFields) return;
                      setValue("endMode", "date", { shouldDirty: true });
                    }}
                    onClick={() => {
                      if (lockRepetitionFields) return;
                      setValue("endMode", "date", { shouldDirty: true });
                    }}
                  />
                </div>
              </div>
              {showErrors && errors.endDate && <FormError message={errors.endDate.message} />}
            </div>
          </>
        )}


        {/* Descripción */}
        <div className={styles.formGroup}>
          <label>
            <p className={`${styles.labelText} ${isDescriptionFocused ? styles.labelFocused : ""}`}>Descripción</p>
          </label>
          <textarea
            className={styles.textarea}
            placeholder="Añade una descripción a la tarea"
            {...register("description")}
            onFocus={() => setIsDescriptionFocused(true)}
            onBlur={() => setIsDescriptionFocused(false)}
          />
        </div>

        {/* Notas */}
        {/* Toggle Notas */}
        <div className={styles.formGroup}>
          {!shouldDisableNotes && (
            <button
              type="button"
              className={`${styles.notesToggle} ${showNotes ? styles.notesToggleActive : ""}`}
              onClick={() => {
                if (lockNotesField) return;
                setShowNotes((v) => !v);
              }}
              onMouseEnter={() => setNotesHover(true)}
              onMouseLeave={() => setNotesHover(false)}
              aria-expanded={showNotes}
              disabled={lockNotesField}
            >
              <img
                className={`${styles.notesToggleIcon} ${showNotes || notesHover ? styles.notesToggleIconRotated : ""}`}
                src={
                  lockNotesField
                    ? NotesHideDisabledIcon
                    : notesHover
                      ? NotesHideHoverIcon
                      : showNotes
                        ? NotesHideFocusIcon
                        : NotesHideIcon
                }
                alt=""
              />
              <span className={styles.notesToggleText}>Añadir nota para el responsable de operaciones</span>
            </button>
          )}

          {showNotes && !shouldDisableNotes && (
            <>
              <label>
                <p className={`${styles.labelText} ${isNotesFocused ? styles.labelFocused : ""}`}>
                  Notas <span className={styles.spanNotes}>(Para el responsable de operaciones)</span>
                </p>
              </label>

              <textarea
                className={styles.textarea}
                placeholder="Añade una nota"
                {...register("notes")}
                disabled={lockNotesField}
                onFocus={() => setIsNotesFocused(true)}
                onBlur={() => setIsNotesFocused(false)}
              />
            </>
          )}
        </div>

        {/* Botón submit */}
        <MainButton
          type="submit"
          variant="submit"
          isSubmitting={isSubmitting}
          disabled={isSubmitting}
          fullWidth
        >
          {taskData ? "Guardar cambios" : "Crear tarea"}
        </MainButton>
      </form>
      {showConfirmEdit && (
        <div className={styles.confirmModal} onClick={() => setShowConfirmEdit(false)}>
          <div className={styles.confirmContent} onClick={(e) => e.stopPropagation()}>

            {/* CASO 1: tarea NO periódica */}
            {modalType === "normal" && (
              <>
                <p className={styles.confirmTitle}>Guardar cambios en la tarea</p>
                <p className={styles.confirmText}>
                  Los cambios realizados se aplicarán a esta tarea.
                </p>
              </>
            )}

            {/* CASO 2: se cambió periodicidad => apply_all true */}
            {modalType === "periodicityChanged" && (
              <>
                <p className={styles.confirmTitle}>Guardar cambios en la tarea</p>
                <p className={styles.confirmText}>
                  Los cambios realizados se aplicarán a esta tarea y a las repeticiones posteriores.
                </p>
              </>
            )}

            {/* CASO 3: tarea periódica sin cambiar periodicidad => selector */}
            {modalType === "scopeSelector" && (
              <>
                <p className={styles.confirmTitle}>¿Dónde quieres aplicar los cambios?</p>

                <div className={styles.deleteScopeBox}>
                  <label className={styles.deleteScopeOption}>
                    <input
                      type="radio"
                      name={`editScope-${taskData?.id || selectedDate}`}
                      value="single"
                      checked={editScope === "single"}
                      onChange={() => setEditScope("single")}
                    />
                    <span className={styles.radioDot} />
                    <span className={styles.deleteScopeText}>Esta tarea</span>
                  </label>

                  <label className={styles.deleteScopeOption}>
                    <input
                      type="radio"
                      name={`editScope-${taskData?.id || selectedDate}`}
                      value="all"
                      checked={editScope === "all"}
                      onChange={() => setEditScope("all")}
                    />
                    <span className={styles.radioDot} />
                    <span className={styles.deleteScopeText}>Esta tarea y las posteriores</span>
                  </label>
                </div>
              </>
            )}

            <div className={styles.confirmButtons}>
              <button type="button" className={styles.cancelButton} onClick={handleCancelEdit}>
                Cancelar
              </button>

              <button type="button" className={styles.deleteButton} onClick={handleConfirmEdit}>
                Guardar cambios
              </button>
            </div>

          </div>
        </div>
      )}
      <Modal
        isOpen={showCloseConfirm}
        onRequestClose={() => setShowCloseConfirm(false)} // click fuera o ESC => solo cierra confirm
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        className={styles.confirmContent}
        overlayClassName={styles.confirmModal}
      >
        <p className={styles.confirmTitle}>Cerrar sin guardar cambios</p>
        <p className={styles.confirmText}>
          Has realizado cambios que no se han guardado. Si cierras ahora, se perderán.
        </p>

        <div className={styles.confirmButtons}>
          <button
            type="button"
            className={styles.keepEditingButton}
            onClick={() => setShowCloseConfirm(false)}
          >
            Seguir editando
          </button>

          <button
            type="button"
            className={styles.confirmCloseButton}
            onClick={() => {
              setShowCloseConfirm(false);
              onClose?.();
            }}
          >
            Descartar cambios
          </button>
        </div>
      </Modal>
    </Modal >
  );
};

export default TaskCreationModal;
